<template>
  <!-- text-xs on the shell: every child sets it too, so anything the SHELL
       itself renders (the Input banner) would otherwise be the only markup here
       at the page default size. -->
  <div class="text-xs">
    <!-- Patient detail REPLACES the tabs rather than nesting inside one: you
         arrive here from search OR from a contract roster, so it is not a child
         of either. `from` remembers which, so Back returns where you came. -->
    <MDR_Patient
      v-if="openPatient"
      :patient-master="openPatient"
      @back="closePatient"
    />

    <template v-else>
      <Page_Tabs
        :tabs="tabs"
        :initial-tab="activeTab"
        class="pb-3"
        no-border
        @select="activeTab = $event"
      />

      <!-- Kept mounted behind v-show: returning to the contract grid is then
           instant and needs no refetch (it is the slowest of the three). -->
      <div v-if="visited.has('Search')" v-show="activeTab === 'Search'">
        <MDR_Search @open="openFromSearch" />
      </div>
      <div v-if="visited.has('Contracts')" v-show="activeTab === 'Contracts'">
        <MDR_Contracts @open-patient="openFromRoster" />
      </div>
      <div v-if="visited.has('Feeds')" v-show="activeTab === 'Feeds'">
        <MDR_Feeds />
      </div>
      <div v-if="visited.has('Input')" v-show="activeTab === 'Input'">
        <!-- THE ONLY WRITE SURFACE IN THIS MODULE. Everything else reads the
             published run; this edits the live PATIENT_ALL view that FEEDS the
             next run, so a bad save breaks the Saturday build rather than this
             screen. Superuser-only and deliberately last in the tab order. -->
        <div
          class="mb-3 px-3 py-2 border border-amber-200 bg-amber-50 rounded
                 text-amber-800"
        >
          Editing the live feeder view. A save redeploys
          <span class="font-mono">EMPI_INPUT.PATIENT_ALL</span> immediately and
          changes what the next weekly run ingests — it does not affect the
          published data on the other tabs.
        </div>
        <MDR_Input />
      </div>
    </template>
  </div>
</template>

<script>
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';
import MDR_Search from '@/components/ETL_Utility/mdr/MDR_Search.vue';
import MDR_Contracts from '@/components/ETL_Utility/mdr/MDR_Contracts.vue';
import MDR_Feeds from '@/components/ETL_Utility/mdr/MDR_Feeds.vue';
import MDR_Patient from '@/components/ETL_Utility/mdr/MDR_Patient.vue';
import MDR_Input from '@/components/ETL_Utility/mdr/MDR_Input.vue';
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';

export default {
  name: 'MDR',
  components: {
    Page_Tabs,
    MDR_Search,
    MDR_Contracts,
    MDR_Feeds,
    MDR_Patient,
    MDR_Input,
  },
  data() {
    return {
      activeTab: null,
      visited: new Set(),
      openPatient: null,
      from: null,
    };
  },
  computed: {
    // Search and the patient record are PHI. Hide the tab rather than letting
    // the user click into a 403 — the backend enforces it either way
    // (views._EMPIPHIView); this only avoids a dead end.
    //
    // MIRRORS THE BACKEND RULE, which is not simply "require data_phi":
    // tenants model PHI differently. IHCI grants module_data_mdr to four roles
    // and has no data_phi row at all, so requiring it would hide search from
    // every MDR user they have. If the tenant publishes data_phi in its feature
    // set, the user must hold it; if the tenant does not use data_phi, MDR
    // access is the decision. Read from tenantAccess.features, which is the
    // tenant's own enabled set.
    isSuperuser() {
      return !!this.$store.getters['auth/current_user']?.is_superuser;
    },
    tenantUsesPhi() {
      const slug = this.$store.getters['tenant/current_tenant_slug'];
      const features = this.$store.state.tenant.tenantAccess?.[slug]?.features;
      // A user whose own grants include it proves the tenant publishes it.
      return Array.isArray(features) && features.includes(FEATURES.DATA_PHI);
    },
    canSeePatients() {
      if (this.isSuperuser) return true;
      // canAccessFeature reads the same precomputed set; when the tenant does
      // not model PHI at all this is false for everyone, so fall back to the
      // MDR grant that got them onto this tab.
      return this.tenantUsesPhi
        ? canAccessFeature(FEATURES.DATA_PHI)
        : canAccessFeature(FEATURES.MODULE_DATA_MDR);
    },
    tabs() {
      const t = [];
      if (this.canSeePatients) {
        t.push({ label: 'Search', icon: ['fas', 'magnifying-glass'] });
      }
      t.push({ label: 'Contracts', icon: ['fas', 'file-contract'] });
      t.push({ label: 'Feeds', icon: ['fas', 'database'] });
      // Input edits the feeder view, not tenant data, so it is gated on
      // is_superuser rather than a feature flag — same rule it had as its own
      // top-level tab. Last in the order because it is the only writer.
      if (this.isSuperuser) {
        t.push({ label: 'Input', icon: ['fas', 'pen-to-square'] });
      }
      return t;
    },
  },
  watch: {
    activeTab: {
      immediate: true,
      handler(t) {
        if (t) this.visited.add(t);
      },
    },
  },
  created() {
    this.activeTab = this.canSeePatients ? 'Search' : 'Contracts';
  },
  methods: {
    openFromSearch(pm) {
      this.from = 'Search';
      this.openPatient = pm;
    },
    openFromRoster(pm) {
      this.from = 'Contracts';
      this.openPatient = pm;
    },
    closePatient() {
      this.openPatient = null;
      if (this.from) this.activeTab = this.from;
    },
  },
};
</script>
